import {AutoSizeOptions, BaseMyopComponent} from "./IMyopComponent";
import {
    BaseMyopMessage,
    ExecuteComponentMethod,
    ExecuteScriptMessage, MessageDirection
} from "../../messages";
import {CleanupHandler, IComponentDefinitionConfig, ISkinConfig} from "../../common";
import {setDebugStyle} from "../componentCore";
import {MyopMessages} from "../../common/MyopMessages.ts";
import {loaderOptions} from "../hostSDK.ts";

export class MyopIframeComponent extends BaseMyopComponent {

    private _autoSizeCleanup?: CleanupHandler;
    private _autoSizeOptions?: AutoSizeOptions;
    private _autoSizeUpdate?: () => void;

    constructor(public componentConfig: IComponentDefinitionConfig, skin: ISkinConfig, public container: HTMLElement, private IframeElement: HTMLIFrameElement, options?: loaderOptions) {
        super(componentConfig, container, options)

        this.element = this.IframeElement;
        // this.IframeElement.addEventListener('error', () => {
        //     if (this._whenInitiatedReject)
        //         this._whenInitiatedReject(`IframeElement error`);
        // });
    }

    sizeMeta?: { width?: string | number; height?: string | number; minWidth?: number; maxWidth?: number; minHeight?: number; maxHeight?: number } | null;

    cleanupInspect?: CleanupHandler;

    inspect() {
        // TODO : use better check for inspect mode
        if (this.cleanupInspect) {
            //console.log('inspect call more than once');
            return this.cleanupInspect
        }
        this.cleanupInspect = setDebugStyle(this.id, 'MyopIframeComponent', 10, this.container, this.IframeElement, this.componentDefinition);
        return () => {
            this.cleanupInspect!();
            this.cleanupInspect = undefined;
        };
    }

    hide() {
        this.IframeElement.style.opacity = '0';
        this.IframeElement.style.position = 'absolute'
        this.IframeElement.style.pointerEvents = 'none'
        this.IframeElement.style.visibility = 'hidden';
    }

    show() {
        this.IframeElement.style.opacity = '1';
        this.IframeElement.style.position = 'unset'
        this.IframeElement.style.pointerEvents = 'all'
        this.IframeElement.style.visibility = 'visible';
    }

    setHeightBasedOnDocumentElement = () => {
        const cleanupHandler = this.send(new ExecuteScriptMessage(() => {
            return window.document.documentElement.scrollHeight;
        }, (ret) => {
            this.IframeElement.style.height = `${ret.content}px`;
            cleanupHandler();
        }));
    }

    observeSizeBasedOnDocumentElement = () => {
        const cleanupHandler = this.send(new ExecuteScriptMessage(({send}) => {
            const {height, width} = document.documentElement.getBoundingClientRect();

            const observer = new ResizeObserver(() => {
                const {height, width} = document.documentElement.getBoundingClientRect();
                send({height, width});
            });
            observer.observe(document.documentElement);
            observer.observe(document.body);

            return {height, width}
        }, (ret) => {
            this.IframeElement.style.width = `${ret.content.width}px`;
            this.IframeElement.style.height = `${ret.content.height}px`;
        }));

        return cleanupHandler;
    }

    /**
     * Observes the iframe content size and automatically syncs it to the iframe element.
     * Uses direct contentDocument access (same-origin only) for accurate measurements.
     *
     * Reentrant: when called again while observers already exist, updates options and
     * re-evaluates sizing without recreating observers. Cleanup + re-call (e.g. React
     * re-render) creates fresh observers since cleanup clears the existing ones.
     *
     * @param options - Configuration options including size change callback and min/max constraints
     * @returns Cleanup function to stop observing
     */
    observeAutoSize = (options?: AutoSizeOptions): CleanupHandler => {
        // Store options so updateSize always reads the latest
        this._autoSizeOptions = options;

        // If observers already exist, re-evaluate with new options and reuse them
        if (this._autoSizeCleanup && this._autoSizeUpdate) {
            this._autoSizeUpdate();
            return this._autoSizeCleanup;
        }

        const iframe = this.IframeElement;
        const doc = iframe.contentDocument;

        if (!doc) {
            console.warn('[MyopIframeComponent] Cannot observe auto size: contentDocument not accessible');
            return () => {};
        }

        const clamp = (value: number, min?: number, max?: number): number => {
            if (min !== undefined && value < min) return min;
            if (max !== undefined && value > max) return max;
            return value;
        };

        // Parse CSS size values to numbers
        const parseSize = (value: string | number | undefined): number | undefined => {
            if (value === undefined) return undefined;
            if (typeof value === "number") return value;
            const parsed = parseFloat(value);
            return isNaN(parsed) ? undefined : parsed;
        };

        const updateSize = () => {
            const contentDoc = iframe.contentDocument;
            if (!contentDoc) return;

            const opts = this._autoSizeOptions;

            const minWidth = parseSize(opts?.minWidth);
            const maxWidth = parseSize(opts?.maxWidth);
            const minHeight = parseSize(opts?.minHeight);
            const maxHeight = parseSize(opts?.maxHeight);
            const loaderMinHeight = opts?.loaderMinHeight ?? 50;

            // Resolve explicit dimensions: host options → sizeMeta fill mode → width defaults to "100%"
            // Width defaults to "100%" (block element behavior) to prevent the shrink-to-0 measurement bug.
            // Height has no default so it auto-sizes to content.
            // Only sizeMeta "100%" (fill mode) is treated as explicit and disables auto-sizing.
            // Pixel sizeMeta values (e.g., height: 200) are initial hints, NOT explicit constraints.
            const sizeMetaFillWidth = this.sizeMeta?.width === "100%" ? "100%" : undefined;
            const sizeMetaFillHeight = this.sizeMeta?.height === "100%" ? "100%" : undefined;
            const resolvedExplicitWidth = opts?.explicitWidth ?? sizeMetaFillWidth ?? "100%";
            const resolvedExplicitHeight = opts?.explicitHeight ?? sizeMetaFillHeight;
            const hasExplicitWidth = resolvedExplicitWidth !== undefined;
            const hasExplicitHeight = resolvedExplicitHeight !== undefined;

            // Detect collapsed dimensions from container (this.container)
            const containerRect = this.container.getBoundingClientRect();
            const collapsedWidth = containerRect.width === 0;
            // Consider collapsed if height is 0 OR equals the loader minHeight
            const collapsedHeight = containerRect.height === 0 || containerRect.height === loaderMinHeight;

            // Determine which dimensions to auto-size:
            // - If forceAutoSize is true, auto-size unless explicit dimension is set
            // - Otherwise, auto-size only if collapsed
            const shouldAutoWidth = opts?.forceAutoSize
                ? !hasExplicitWidth
                : (collapsedWidth && !hasExplicitWidth);
            const shouldAutoHeight = opts?.forceAutoSize
                ? !hasExplicitHeight
                : (collapsedHeight && !hasExplicitHeight);

            // If nothing needs auto-sizing, apply explicit/percentage dimensions
            if (!shouldAutoWidth && !shouldAutoHeight) {
                iframe.style.width = typeof resolvedExplicitWidth === 'number' ? `${resolvedExplicitWidth}px` : '100%';
                iframe.style.height = typeof resolvedExplicitHeight === 'number' ? `${resolvedExplicitHeight}px` : '100%';

                opts?.onSizeChange?.({
                    width: typeof resolvedExplicitWidth === 'number' ? resolvedExplicitWidth : undefined,
                    height: typeof resolvedExplicitHeight === 'number' ? resolvedExplicitHeight : undefined,
                    autoSizingWidth: false,
                    autoSizingHeight: false,
                });
                return;
            }

            // Disable CSS transitions on iframe and container for the entire
            // measurement + height-setting cycle. Host apps may set `transition: all`
            // which would animate the 0px collapse and final height change, causing
            // visible jumps during scroll.
            const prevTransition = iframe.style.transition;
            const container = this.container;
            const prevContainerTransition = container.style.transition;
            iframe.style.transition = 'none';
            container.style.transition = 'none';

            // Quick pre-check: read scrollHeight/Width BEFORE collapsing the iframe.
            // If content only grew (scrollHeight > iframe height), we can skip the
            // 0px collapse — scrollHeight naturally expands beyond the iframe.
            const currentIframeHeight = iframe.getBoundingClientRect().height;
            const currentIframeWidth = iframe.getBoundingClientRect().width;
            const preScrollHeight = Math.ceil(Math.max(contentDoc.documentElement.scrollHeight, contentDoc.body.scrollHeight, contentDoc.body.getBoundingClientRect().height));
            const preScrollWidth = Math.ceil(Math.max(contentDoc.documentElement.scrollWidth, contentDoc.body.scrollWidth, contentDoc.body.getBoundingClientRect().width));

            let contentWidth = preScrollWidth;
            let contentHeight = preScrollHeight;

            const heightGrew = shouldAutoHeight && preScrollHeight > currentIframeHeight + 1;
            const widthGrew = shouldAutoWidth && preScrollWidth > currentIframeWidth + 1;

            // For non-grow cases (shrink or no-change), we must collapse to 0px to
            // measure the true content height. scrollHeight can never decrease below
            // the iframe's clientHeight, so shrink is undetectable without collapsing.
            // With transitions disabled + container min-height held, the collapse
            // is invisible (no layout shift, no animation).
            const needsHeightCollapse = shouldAutoHeight && !heightGrew;
            const needsWidthCollapse = shouldAutoWidth && !widthGrew;

            if (needsHeightCollapse || needsWidthCollapse) {
                // Hold the container's min-height/width during measurement to prevent
                // layout shift (elements below the iframe won't jump).
                const prevMinHeight = container.style.minHeight;
                const prevMinWidth = container.style.minWidth;
                if (needsHeightCollapse) container.style.minHeight = `${currentIframeHeight}px`;
                if (needsWidthCollapse) container.style.minWidth = `${currentIframeWidth}px`;

                if (needsHeightCollapse) iframe.style.height = '0px';
                if (needsWidthCollapse) iframe.style.width = '0px';

                contentWidth = Math.ceil(Math.max(contentDoc.documentElement.scrollWidth, contentDoc.body.scrollWidth, contentDoc.body.getBoundingClientRect().width));
                contentHeight = Math.ceil(Math.max(contentDoc.documentElement.scrollHeight, contentDoc.body.scrollHeight, contentDoc.body.getBoundingClientRect().height));

                // Fallback: if collapsing to 0px gave a near-zero measurement, the component
                // likely has `height: 100%` chains (html > body > .app-container) that cascaded
                // the collapse into all children. Retry with a nuclear override that forces all
                // elements to intrinsic sizing. This is only a fallback because the override
                // breaks explicit pixel heights (e.g. 8px indicator dots → 0px).
                const needsNuclearHeight = needsHeightCollapse && contentHeight < 2;
                const needsNuclearWidth = needsWidthCollapse && contentWidth < 2;
                if (needsNuclearHeight || needsNuclearWidth) {
                    const measureStyle = contentDoc.createElement('style');
                    measureStyle.id = '__myop_measure';
                    const rules: string[] = [];
                    if (needsNuclearHeight) {
                        rules.push('height: auto !important', 'min-height: 0 !important', 'max-height: none !important');
                    }
                    if (needsNuclearWidth) {
                        rules.push('width: auto !important', 'min-width: 0 !important', 'max-width: none !important');
                    }
                    measureStyle.textContent = `*, *::before, *::after { ${rules.join('; ')}; flex-basis: auto !important; flex-grow: 0 !important; }`;
                    contentDoc.head.appendChild(measureStyle);

                    contentWidth = Math.ceil(Math.max(contentDoc.documentElement.scrollWidth, contentDoc.body.scrollWidth, contentDoc.body.getBoundingClientRect().width));
                    contentHeight = Math.ceil(Math.max(contentDoc.documentElement.scrollHeight, contentDoc.body.scrollHeight, contentDoc.body.getBoundingClientRect().height));

                    measureStyle.remove();
                }

                // Restore container constraints immediately (same frame, before paint)
                container.style.minHeight = prevMinHeight;
                container.style.minWidth = prevMinWidth;
            }

            const width = clamp(contentWidth, minWidth, maxWidth);
            const height = clamp(contentHeight, minHeight, maxHeight);

            // Set auto-sized dimensions to measured values, non-auto to explicit/100%
            if (shouldAutoWidth) {
                iframe.style.width = `${width}px`;
            } else {
                iframe.style.width = typeof resolvedExplicitWidth === 'number' ? `${resolvedExplicitWidth}px` : '100%';
            }
            if (shouldAutoHeight) {
                iframe.style.height = `${height}px`;
            } else {
                iframe.style.height = typeof resolvedExplicitHeight === 'number' ? `${resolvedExplicitHeight}px` : '100%';
            }

            // Force reflow so the final dimensions are applied without transition,
            // then restore the original transitions.
            // eslint-disable-next-line no-unused-expressions
            iframe.offsetHeight;
            iframe.style.transition = prevTransition;
            container.style.transition = prevContainerTransition;

            // Don't force overflow — let the component/parent control scrolling.
            // Only set overflow: auto when content exceeds max constraints.
            const isWidthConstrained = shouldAutoWidth && maxWidth !== undefined && contentWidth > maxWidth;
            const isHeightConstrained = shouldAutoHeight && maxHeight !== undefined && contentHeight > maxHeight;
            contentDoc.documentElement.style.overflowX = isWidthConstrained ? 'auto' : '';
            contentDoc.documentElement.style.overflowY = isHeightConstrained ? 'auto' : '';

            opts?.onSizeChange?.({
                width,
                height,
                autoSizingWidth: shouldAutoWidth,
                autoSizingHeight: shouldAutoHeight
            });
        };

        // Initial size (run synchronously, not debounced)
        updateSize();

        // Store updateSize for reentrant calls
        this._autoSizeUpdate = updateSize;

        // Debounce observer callbacks via rAF — at most one updateSize per frame.
        // Prevents layout thrashing from high-frequency mutations (e.g. CSS animations).
        let rafId: number | undefined;
        const scheduleUpdate = () => {
            if (rafId !== undefined) return;
            rafId = requestAnimationFrame(() => {
                rafId = undefined;
                updateSize();
            });
        };

        // Observe for changes
        const resizeObserver = new ResizeObserver(() => scheduleUpdate());
        resizeObserver.observe(doc.body);
        resizeObserver.observe(doc.documentElement);

        // MutationObserver catches DOM content changes (e.g. removing list items, toggling views)
        // that don't trigger ResizeObserver because the body box-size stays the same
        // (body fills iframe, iframe hasn't shrunk yet).
        // `attributes: true` catches CSS class/style changes (e.g. display:none toggling views).
        const mutationObserver = new MutationObserver(() => scheduleUpdate());
        mutationObserver.observe(doc.body, { childList: true, subtree: true, characterData: true, attributes: true });

        const cleanup = () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            if (rafId !== undefined) {
                cancelAnimationFrame(rafId);
                rafId = undefined;
            }
            this._autoSizeUpdate = undefined;
            this._autoSizeCleanup = undefined;
        };
        this._autoSizeCleanup = cleanup;
        return cleanup;
    }

    setSizeBasedOnDocumentElement = () => {
        const cleanupHandler = this.send(new ExecuteScriptMessage(() => {
            const {height, width} = document.documentElement.getBoundingClientRect();
            return {height, width}
        }, (ret) => {
            this.IframeElement.style.width = `${ret.content.width}px`;
            this.IframeElement.style.height = `${ret.content.height}px`;
            cleanupHandler();
        }));
    }

    setHeightBasedOnScrollHeight = () => {
        const cleanupHandler = this.send(new ExecuteScriptMessage(() => {
            // @ts-ignore
            // return window.document.body.scrollHeight + 'px';


            //TODO: fix that hack, keep the function in right format while minified :(
            const noOp = () => {
                // No operation performed
                return 0;
            }
            // To ensure it's not optimized away, you can call it at least once
            let x = noOp();
            x++;
            x--;

            //return `${window.document.body.offsetHeight}px`;

            //TODO : check that +1 ?
            return Math.max(Math.max(
                    window.document.body.clientHeight,
                    window.document.body.scrollHeight)
                , window.document.body.offsetHeight) + x + 'px'

        }, (ret) => {
            this.IframeElement.style.height = ret.content;
            cleanupHandler();
        }));
    }

    send = (message: BaseMyopMessage): CleanupHandler => {
        let message_ = {...message} as BaseMyopMessage;
        //TODO : ?!?! should it be here ?
        if (message_.messageType === MyopMessages.ExecuteComponentMethod) {
            const typedMessage = (message_ as ExecuteComponentMethod)
            this[typedMessage.method]();
            return () => {
            }
        }

        //TODO : fix that hack :(
        // @ts-ignore
        //const handler = message.handler

        message_.direction = MessageDirection.down;
        const cleanupHandler = super.send(message_);
        this.IframeElement?.contentWindow?.postMessage(message_, '*');

        // @ts-ignore
        //message.handler = handler

        return cleanupHandler;
    };

    onMessageReceived(incomeMessage: BaseMyopMessage): boolean {
        if (incomeMessage.messageType === MyopMessages.In.MutationObserverMessage
            || incomeMessage.messageType === MyopMessages.In.ResizeObserverMessage) {
            this.setHeightBasedOnScrollHeight();
            //debugger
            //this.setHeightBasedOnDocumentElement();
            return true;
        }

        return super.onMessageReceived(incomeMessage);
    }

    dispose = () => {
        if (this._autoSizeCleanup) {
            this._autoSizeCleanup();
            this._autoSizeCleanup = undefined;
        }
        if (this.cleanupInspect)
            this.cleanupInspect();

        super.dispose();
        this.IframeElement.parentNode!.removeChild(this.IframeElement);
    }
}
